import { useEffect, useState } from "react";
import { Spinner, Table } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./AdminFunnelPage.module.css";

interface FunnelReport {
  readonly range: { readonly fromDay: string; readonly toDay: string; readonly days: number };
  readonly steps: {
    readonly landingViews: number;
    readonly signupPageViews: number;
    readonly emailsCaptured: number;
    readonly trialsStarted: number;
  };
  readonly rates: {
    readonly landingToSignup: number;
    readonly signupToEmail: number;
    readonly emailToTrial: number;
    readonly landingToTrial: number;
  };
  readonly attribution: ReadonlyArray<{
    readonly source: "paid" | "organic";
    readonly landingViews: number;
    readonly signupPageViews: number;
    readonly emails: number;
    readonly trials: number;
  }>;
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; report: FunnelReport }
  | { kind: "error"; message: string };

const RANGES: ReadonlyArray<{ readonly label: string; readonly days: number }> = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

export function AdminFunnelPage() {
  usePageTitle("Admin · Funnel");
  const [days, setDays] = useState<number>(30);
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetch(`/api/admin/funnel?days=${days}`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
        if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
        const json = await res.json() as { success: boolean; data?: FunnelReport };
        if (json.success && json.data) setState({ kind: "ready", report: json.data });
        else setState({ kind: "error", message: "Unexpected response" });
      })
      .catch(() => { if (!cancelled) setState({ kind: "error", message: "Network error" }); });
    return () => { cancelled = true; };
  }, [days]);

  if (state.kind === "unauthorized") {
    return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;
  }

  return (
    <AdminPage
      title="Funnel"
      aside={state.kind === "ready" ? `${state.report.range.fromDay} to ${state.report.range.toDay}` : undefined}
    >
      <div className={styles.filters}>
        {RANGES.map((r) => (
          <button
            key={r.days}
            type="button"
            className={`${styles.pill} ${days === r.days ? styles.pillActive : ""}`}
            aria-pressed={days === r.days}
            onClick={() => setDays(r.days)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {state.kind === "loading" ? <Spinner /> : null}
      {state.kind === "error" ? <div className={styles.empty}>{state.message}</div> : null}

      {state.kind === "ready" ? (
        <>
          <FunnelSteps report={state.report} />
          <section className={styles.section}>
            <h2 className={styles.sectionHeader}>By source</h2>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Source</Table.HeaderCell>
                  <Table.HeaderCell>Landing</Table.HeaderCell>
                  <Table.HeaderCell>Signup page</Table.HeaderCell>
                  <Table.HeaderCell>Emails</Table.HeaderCell>
                  <Table.HeaderCell>Trials</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {state.report.attribution.map((row) => (
                  <Table.Row key={row.source}>
                    <Table.Cell>{row.source === "paid" ? "Paid (Google Ads)" : "Organic"}</Table.Cell>
                    <Table.Cell className={styles.num}>{row.landingViews}</Table.Cell>
                    <Table.Cell className={styles.num}>{row.signupPageViews}</Table.Cell>
                    <Table.Cell className={styles.num}>{row.emails}</Table.Cell>
                    <Table.Cell className={styles.num}>{row.trials}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </section>
        </>
      ) : null}
    </AdminPage>
  );
}

function FunnelSteps({ report }: { readonly report: FunnelReport }) {
  const { steps, rates } = report;
  const rows: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly count: number;
    readonly rateFromPrev: number | null;
    readonly rateFromTop: number | null;
  }> = [
    { key: "landing", label: "Landing page view", count: steps.landingViews, rateFromPrev: null, rateFromTop: null },
    { key: "signup", label: "Signup page view", count: steps.signupPageViews, rateFromPrev: rates.landingToSignup, rateFromTop: rates.landingToSignup },
    { key: "email", label: "Email captured", count: steps.emailsCaptured, rateFromPrev: rates.signupToEmail, rateFromTop: null },
    { key: "trial", label: "Trial started", count: steps.trialsStarted, rateFromPrev: rates.emailToTrial, rateFromTop: rates.landingToTrial },
  ];
  const top = Math.max(steps.landingViews, 1);
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeader}>Conversion funnel</h2>
      <div className={styles.funnelCard}>
        {rows.map((row) => {
          const widthPct = Math.max(2, Math.round((row.count / top) * 100));
          return (
            <div key={row.key} className={styles.step}>
              <div className={styles.stepLabel}>{row.label}</div>
              <div className={styles.stepBarWrap}>
                <div className={styles.stepBar} style={{ width: `${widthPct}%` }}>
                  <span className={styles.stepCount}>{row.count}</span>
                </div>
              </div>
              <div className={styles.stepMeta}>
                {row.rateFromPrev !== null ? <span>{(row.rateFromPrev * 100).toFixed(1)}% step</span> : null}
                {row.rateFromTop !== null && row.key !== "signup" ? (
                  <span className={styles.metaAccent}>{(row.rateFromTop * 100).toFixed(2)}% overall</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
